import {
  Box,
  Button,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { AddFromSpreadsheetFormValues } from './AddProductsFromSpreadsheet';
import { useEffect, useMemo, useState } from 'react';
import { utils, WorkSheet } from 'xlsx';
import { DoubleArrow } from '@mui/icons-material';

type MatchedHeader = {
  header: string;
  match: string;
  enabled: boolean;
  required: boolean;
};

const getHeaderRow = (sheet: WorkSheet): string[] => {
  const headers = [];
  const columnCount = utils.decode_range(sheet['!ref'] || '').e.c + 1;
  for (let i = 0; i < columnCount; ++i) {
    const cell = sheet[`${utils.encode_col(i)}1`];
    headers[i] = cell?.v || `Column ${i + 1}`;
  }
  return headers;
};

const renameHeadersInSheet = (
  sheet: WorkSheet,
  matchedHeaders: MatchedHeader[],
  spreadsheetHeaders: string[]
) => {
  matchedHeaders.forEach((header) => {
    if (!header.match) return;
    const columnIndex = spreadsheetHeaders.indexOf(header.match);
    if (columnIndex < 0) return;
    sheet[`${utils.encode_col(columnIndex)}1`] = { t: 's', v: header.header };
  });
};

const convertColumnToString = (sheet: WorkSheet, columnIndex: number) => {
  const range = utils.decode_range(sheet['!ref'] || '');
  for (let row = 1; row <= range.e.r; row++) {
    const cellAddress = `${utils.encode_col(columnIndex)}${row + 1}`;
    const cell = sheet[cellAddress];
    if (cell && cell.v !== undefined) {
      sheet[cellAddress] = { t: 's', v: String(cell.v) };
    }
  }
};

const MatchHeaders = ({
  values,
  setFieldValue,
}: FormikProps<AddFromSpreadsheetFormValues>) => {
  const [matchedHeaders, setMatchedHeaders] = useState<MatchedHeader[]>([
    { header: 'sku', match: '', enabled: true, required: true },
    { header: 'price', match: '', enabled: false, required: false },
    { header: 'title', match: '', enabled: false, required: false },
    { header: 'description', match: '', enabled: false, required: false },
    { header: 'categories', match: '', enabled: false, required: false },
  ]);

  const spreadsheetHeaders = useMemo(
    () => (values.spreadsheet ? getHeaderRow(values.spreadsheet) : []),
    [values.spreadsheet]
  );

  useEffect(() => {
    setMatchedHeaders((prev) =>
      prev.map((header) => {
        const match =
          spreadsheetHeaders.find(
            (spreadsheetHeader) =>
              spreadsheetHeader.trim().toLowerCase() === header.header
          ) || '';
        return {
          ...header,
          match,
          enabled: header.required || !!match,
        };
      })
    );
  }, [spreadsheetHeaders]);

  const handleNext = () => {
    if (!values.spreadsheet) return;
    const sheet = values.spreadsheet;

    // Rename column headers to matched field names
    renameHeadersInSheet(sheet, matchedHeaders, spreadsheetHeaders);

    // Convert SKU column cells to string
    const skuColumnIndex = spreadsheetHeaders.indexOf(
      matchedHeaders.find((h) => h.header === 'sku')?.match || ''
    );
    if (skuColumnIndex < 0)
      return alert('Your spreadsheet is missing a SKU column');
    convertColumnToString(sheet, skuColumnIndex);

    const matchedFieldNames = matchedHeaders
      .filter((h) => h.match)
      .map((h) => h.header);

    // Cleanup missing fields
    const missingSkus: number[] = [];
    const missingPrices: number[] = [];
    const validatedProducts = utils
      .sheet_to_json(sheet, {
        raw: false,
      })
      .map((product: any, i) => {
        if (!product.sku) {
          missingSkus.push(i);
          return null;
        }
        const formattedProduct: Record<string, string> = {};
        matchedFieldNames.forEach((field) => {
          if (field in product) {
            formattedProduct[field] = product[field];
          }
        });
        // Sanitize price to only include numbers and decimal point
        if (formattedProduct.price) {
          formattedProduct.price = formattedProduct.price.replace(
            /[^0-9.]/g,
            ''
          );
        }
        if (!formattedProduct.price) {
          formattedProduct.price = '0';
          missingPrices.push(i);
        }
        formattedProduct.index = `${i}`;
        return formattedProduct;
      })
      .filter((product) => product);
    if (missingSkus.length || missingPrices.length)
      alert(
        (missingSkus.length
          ? `The following rows were skipped because they are missing a SKU: ${missingSkus
              .map((i) => i + 2)
              .join(', ')}\n\n`
          : '') +
          (missingPrices.length
            ? `The following rows had their price set to zero because they were missing a price: ${missingPrices
                .map((i) => i + 2)
                .join(', ')}\n`
            : '')
      );
    setFieldValue('products', validatedProducts);
    setFieldValue('activeStep', 2);
  };

  return (
    <Stack>
      <Typography variant="h4">
        Match the columns from your file with our system
      </Typography>
      <List>
        {matchedHeaders.map(({ header, match, enabled, required }, i) => (
          <ListItem
            divider
            alignItems="center"
            key={header}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Stack flex="1 1 0">
              <Typography textTransform="capitalize">{header}</Typography>
              {required ? (
                <Typography variant="caption">Required</Typography>
              ) : null}
            </Stack>
            <DoubleArrow
              htmlColor="#0000008F"
              sx={{ mx: 4, height: 24, width: 24 }}
            />
            <Select
              value={match}
              onChange={(event) =>
                setMatchedHeaders((prev) => {
                  const current = [...prev];
                  current[i] = {
                    ...prev[i],
                    match: event.target.value,
                    enabled: true,
                  };
                  return current;
                })
              }
              displayEmpty
              renderValue={(value) =>
                value || (
                  <Typography component="span" color="text.secondary">
                    Column
                  </Typography>
                )
              }
              sx={{
                flex: '1 1 0',
              }}
            >
              {spreadsheetHeaders.map((header) => (
                <MenuItem key={header} value={header}>
                  {header}
                </MenuItem>
              ))}
            </Select>
            <Switch
              checked={enabled}
              disabled={required}
              onChange={() =>
                setMatchedHeaders((prev) => {
                  const current = [...prev];
                  current[i] = {
                    ...prev[i],
                    match: prev[i].enabled ? '' : prev[i].match,
                    enabled: !prev[i].enabled,
                  };
                  return current;
                })
              }
              sx={{
                mx: 4,
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box alignSelf="flex-end">
        <Button
          variant="flat"
          onClick={handleNext}
          disabled={matchedHeaders.some(
            ({ enabled, match, required }) => (enabled || required) && !match
          )}
        >
          Next
        </Button>
      </Box>
    </Stack>
  );
};

export default MatchHeaders;
